/**
 * Single source of truth for "how is `position.top/left/right/bottom` on a
 * page-level floating element interpreted at render time?"
 *
 * Three storage / render eras exist:
 *
 *   ┌──────────┬─────────────────────────┬──────────────────────────────────┐
 *   │ Version  │ Storage origin          │ Renderer behavior                │
 *   ├──────────┼─────────────────────────┼──────────────────────────────────┤
 *   │ v1       │ Page-relative           │ `preferBottom` / `preferRight`   │
 *   │          │ (anchored to `.page-    │ flags ARE honored — element      │
 *   │          │  view` origin)          │ may anchor to top / bottom and   │
 *   │          │                         │ left / right of the page-level   │
 *   │          │                         │ box. Legacy `else` branch.       │
 *   ├──────────┼─────────────────────────┼──────────────────────────────────┤
 *   │ v2       │ Page-relative           │ Simple `top` + `left` ONLY —     │
 *   │          │ (anchored to `.page-    │ matches v2-era authoring code,   │
 *   │          │  view` origin)          │ which never read prefer flags    │
 *   │          │                         │ for v2 docs even when present    │
 *   │          │                         │ in the data (e.g. from a more    │
 *   │          │                         │ recent re-save).                 │
 *   ├──────────┼─────────────────────────┼──────────────────────────────────┤
 *   │ v3+      │ Content-relative        │ `contentAreaOffset` is added on  │
 *   │          │ (anchored to `.page-    │ top of stored coords; PDF view   │
 *   │          │  content` origin)       │ also runs the page-level anchor  │
 *   │          │                         │ + clone pipeline.                │
 *   └──────────┴─────────────────────────┴──────────────────────────────────┘
 *
 * Why v1 and v2 differ even though both store page-relative coords:
 *   v1 was authored with the legacy renderer that respected `preferBottom`
 *   and `preferRight`. v2 introduced its own dedicated render path that
 *   ignored those flags and used raw `top` + `left`. Forcing both versions
 *   through one branch breaks documents authored under either rule —
 *   v1 docs lose their bottom / right anchor, v2 docs flip a stored
 *   `preferRight: true` into a `right:`-anchored render that didn't exist
 *   in v2 era.
 *
 * Use these helpers at every position read/render site that has to decide
 * which interpretation to apply. Do NOT inline the version checks — keeping
 * the gate in one place lets us bump thresholds in a single edit.
 *
 * NOT for typography, fonts, or other version-specific differences — those
 * have their own checks.
 */
export const PAGE_LEVEL_FLOAT_CONTENT_RELATIVE_VERSION = 3
export const PAGE_LEVEL_FLOAT_PAGE_RELATIVE_SIMPLE_VERSION = 2

/**
 * @returns true when the document stores page-level floating element
 *   positions content-relative (i.e. v3+); false when positions are
 *   page-relative (legacy v1 / v2).
 */
export function usesContentRelativePagePositioning(version: number | null | undefined): boolean {
  return (version ?? 1) >= PAGE_LEVEL_FLOAT_CONTENT_RELATIVE_VERSION
}

/**
 * @returns true when the document is exactly v2 — the dedicated v2-era render
 *   path applies (page-relative origin, simple `top`/`left` only, prefer
 *   flags ignored even if present). v1 documents fall through to the
 *   original legacy renderer; v3+ documents use the content-relative path.
 */
export function usesPageRelativeSimplePositioning(version: number | null | undefined): boolean {
  return (version ?? 1) === PAGE_LEVEL_FLOAT_PAGE_RELATIVE_SIMPLE_VERSION
}
